/**
 * ============================================================================
 * CEDARWEL · EXIT INTENT SURVEY
 * ----------------------------------------------------------------------------
 * Vanilla ES6+. No dependencies. No globals except the small public API
 * exposed as `window.CedarwelExitSurvey` (used for QA and debugging).
 *
 * Responsibilities
 *   1. Exit detection
 *        · Mobile: back button / swipe-back (History API sentinel),
 *          rapid upward scroll (velocity), inactivity, tab return.
 *        · Desktop: pointer leaving through the top of the viewport.
 *   2. Frequency governance — once per session (sessionStorage), day-based
 *      caps and a post-submission quiet period (localStorage).
 *   3. Dialog lifecycle — scroll lock, focus trap, staggered entrance,
 *      graceful exit, reduced-motion support, Theme Editor preview.
 *   4. Response capture — answer, free text, device/browser/OS, referrer,
 *      UTM set, session id, viewport/screen, engagement metrics.
 *   5. Delivery — Shopify's native contact form (AJAX), dataLayer event,
 *      and a DOM CustomEvent for any listening scripts.
 *
 * All visual state is class-driven; the only inline styles this module writes
 * are (a) the body scroll-lock offset and (b) the `--cw-i` stagger index —
 * both are runtime values that cannot live in a stylesheet.
 * ============================================================================
 */
(() => {
  'use strict';

  // --------------------------------------------------------------------------
  // Storage keys & safe wrappers (private browsing can throw on access)
  // --------------------------------------------------------------------------
  const KEYS = {
    shown: 'cw:exit:shown', // sessionStorage — once-per-session gate
    sessionId: 'cw:exit:sessionId', // sessionStorage
    utm: 'cw:exit:utm', // sessionStorage — UTM set captured on landing
    landingRef: 'cw:exit:landingRef', // sessionStorage — first referrer seen
    lastShown: 'cw:exit:lastShown', // localStorage — frequency cap
    submittedAt: 'cw:exit:submittedAt', // localStorage — quiet period
    addedToCart: 'cw:exit:addedToCart', // localStorage
    reachedCheckout: 'cw:exit:reachedCheckout' // localStorage
  };

  const store = (backend) => ({
    get(key) {
      try {
        return backend.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        backend.setItem(key, value);
      } catch (_) {
        /* storage unavailable — degrade silently */
      }
    }
  });

  const session = store(window.sessionStorage);
  const local = store(window.localStorage);

  // --------------------------------------------------------------------------
  // Small utilities
  // --------------------------------------------------------------------------
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const uuid = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // RFC4122-ish fallback for older WebViews
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  };

  const pad2 = (n) => String(n).padStart(2, '0');

  const formatClock = (ms) => {
    const total = Math.round(ms / 1000);
    return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
  };

  /**
   * Honest, lightweight UA parsing — enough for a feedback report, not
   * fingerprinting. Prefers Client Hints where available.
   */
  const detectEnvironment = () => {
    const ua = navigator.userAgent || '';
    const uad = navigator.userAgentData;

    let os = 'Unknown';
    if (/Android/i.test(ua)) os = 'Android';
    else if (/iPhone|iPod/.test(ua)) os = 'iOS';
    else if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) os = 'iPadOS';
    else if (/Windows/.test(ua)) os = 'Windows';
    else if (/Macintosh/.test(ua)) os = 'macOS';
    else if (/CrOS/.test(ua)) os = 'ChromeOS';
    else if (/Linux/.test(ua)) os = 'Linux';
    if (uad && uad.platform) os = uad.platform;

    let browser = 'Unknown';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/SamsungBrowser/.test(ua)) browser = 'Samsung Internet';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/CriOS/.test(ua)) browser = 'Chrome (iOS)';
    else if (/FxiOS/.test(ua)) browser = 'Firefox (iOS)';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Chrome\//.test(ua)) browser = 'Chrome';
    else if (/Safari\//.test(ua)) browser = 'Safari';

    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const narrow = window.innerWidth < 750;
    const device = coarse && narrow ? 'Mobile' : coarse ? 'Tablet' : 'Desktop';

    return { os, browser, device, coarse };
  };

  // ==========================================================================
  // ExitSurvey
  // ==========================================================================
  class ExitSurvey {
    constructor(root) {
      this.root = root;

      const configEl = root.querySelector('[data-cw-config]');
      if (!configEl) return;
      try {
        this.cfg = JSON.parse(configEl.textContent);
      } catch (_) {
        return;
      }

      // ---- Element handles -------------------------------------------------
      this.sheet = root.querySelector('[data-cw-sheet]');
      this.overlay = root.querySelector('[data-cw-overlay]');
      this.form = root.querySelector('[data-cw-form]');
      this.submitBtn = root.querySelector('[data-cw-submit]');
      this.reveal = root.querySelector('[data-cw-reveal]');
      this.textarea = root.querySelector('[data-cw-textarea]');
      this.counter = root.querySelector('[data-cw-counter]');
      this.successPanel = root.querySelector('[data-cw-panel="success"]');
      this.contactForm = document.getElementById(this.cfg.contactFormId);

      // ---- State -----------------------------------------------------------
      this.env = detectEnvironment();
      this.debug = this.cfg.debug || /[?&]cw_exit_debug=1/.test(location.search);
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.armed = false;
      this.isOpen = false;
      this.isClosing = false;
      this.submitted = false;
      this.openReason = null;

      this.sentinelPushed = false;
      this.dialogStatePushed = false;
      this.awaitingHistoryClose = false;
      this.pendingTabReturn = false;
      this.hasShown = false;

      this.maxScrollDepth = 0;
      this.lastScrollY = window.scrollY;
      this.lastScrollT = performance.now();
      this.scrollTicking = false;

      this.visibleSince = performance.now();
      this.activeMs = 0; // accumulated tab-visible time

      this.idleTimer = null;
      this.autocloseTimer = null;
      this.lockScrollY = 0;
      this.lastFocused = null;

      // ---- Bound handlers (kept for add/removeEventListener symmetry) ------
      this.onScroll = this.onScroll.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onActivity = this.onActivity.bind(this);
      this.onMouseOut = this.onMouseOut.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocClick = this.onDocClick.bind(this);

      this.init();
    }

    log(...args) {
      if (this.debug) console.info('[Cedarwel Exit Survey]', ...args);
    }

    // ------------------------------------------------------------------------
    // Init
    // ------------------------------------------------------------------------
    init() {
      this.ensureSessionId();
      this.captureUtm();
      this.captureLandingReferrer();
      this.assignStaggerIndexes();
      this.applyReducedMotion();
      this.bindFormEvents();
      this.bindDesignMode();

      // Engagement metrics + funnel signals are tracked regardless of
      // eligibility — they feed the report if the survey ever shows.
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('click', this.onDocClick, { capture: true, passive: true });

      if (this.designMode) {
        this.log('Theme Editor detected — triggers disabled, preview via section select.');
        return;
      }
      if (!this.deviceEligible()) {
        this.log('Device not eligible', this.env.device);
        return;
      }
      if (!this.passesCaps()) return;

      const delay = Math.max(0, Number(this.cfg.initialDelay) || 0) * 1000;
      window.setTimeout(() => this.arm(), delay);
    }

    deviceEligible() {
      const mobileLike = this.env.device !== 'Desktop';
      return mobileLike ? this.cfg.enableMobile : this.cfg.enableDesktop;
    }

    passesCaps() {
      if (this.debug && /cw_exit_debug=1/.test(location.search)) {
        this.log('Debug URL flag — caps bypassed.');
        return true;
      }

      const now = Date.now();
      const day = 86400000;

      const submittedAt = Number(local.get(KEYS.submittedAt));
      if (
        submittedAt &&
        this.cfg.suppressAfterSubmitDays > 0 &&
        now - submittedAt < this.cfg.suppressAfterSubmitDays * day
      ) {
        this.log('Within post-submission quiet period.');
        return false;
      }

      if (this.cfg.oncePerSession && session.get(KEYS.shown)) {
        this.log('Already shown this session.');
        return false;
      }

      const lastShown = Number(local.get(KEYS.lastShown));
      if (lastShown && this.cfg.frequencyDays > 0 && now - lastShown < this.cfg.frequencyDays * day) {
        this.log('Within frequency cap window.');
        return false;
      }

      return true;
    }

    // ------------------------------------------------------------------------
    // Arming & triggers
    // ------------------------------------------------------------------------
    arm() {
      if (this.armed) return;
      this.armed = true;
      this.log('Armed', { env: this.env });

      if (this.cfg.triggerBack && this.env.coarse) this.armBackSentinel();
      if (this.cfg.triggerInactivity) this.armInactivity();
      if (this.cfg.triggerMouseExit && !this.env.coarse) {
        document.addEventListener('mouseout', this.onMouseOut);
      }
      // Tab-return + scroll-up piggyback on listeners attached in init().
    }

    /**
     * Push one duplicate history entry ("sentinel"). The first back gesture
     * (Android button or iOS edge swipe) pops it — the visitor stays on the
     * page and we surface the survey. Every subsequent back gesture behaves
     * natively. One interception, never a trap.
     */
    armBackSentinel() {
      try {
        history.pushState({ cwExitSentinel: true }, '', location.href);
        this.sentinelPushed = true;
        window.addEventListener('popstate', this.onPopState);
        this.log('Back sentinel armed.');
      } catch (_) {
        this.log('History API unavailable — back trigger skipped.');
      }
    }

    onPopState() {
      // Back pressed while the dialog is open → close the dialog, stay on page.
      if (this.isOpen) {
        this.dialogStatePushed = false;
        this.close(true);
        return;
      }
      if (this.sentinelPushed) {
        this.sentinelPushed = false;
        this.maybeOpen('back_button');
      }
    }

    armInactivity() {
      const events = ['scroll', 'touchstart', 'pointermove', 'keydown'];
      events.forEach((name) =>
        document.addEventListener(name, this.onActivity, { passive: true })
      );
      this.resetIdleTimer();
    }

    onActivity() {
      if (!this.isOpen) this.resetIdleTimer();
    }

    resetIdleTimer() {
      window.clearTimeout(this.idleTimer);
      if (!this.cfg.triggerInactivity || this.isOpen) return;
      this.idleTimer = window.setTimeout(() => {
        if (document.visibilityState === 'visible') this.maybeOpen('inactivity');
      }, Math.max(5, this.cfg.inactivitySeconds) * 1000);
    }

    onVisibility() {
      if (document.visibilityState === 'hidden') {
        this.activeMs += performance.now() - this.visibleSince;
        window.clearTimeout(this.idleTimer);
        if (this.armed && this.cfg.triggerTabReturn && !this.isOpen) {
          this.pendingTabReturn = true;
        }
      } else {
        this.visibleSince = performance.now();
        this.resetIdleTimer();
        if (this.pendingTabReturn) {
          this.pendingTabReturn = false;
          // Small beat so the return doesn't feel ambushed.
          window.setTimeout(() => this.maybeOpen('tab_return'), 350);
        }
      }
    }

    /**
     * Scroll handler does double duty on one rAF budget:
     *   · records max scroll depth for the report;
     *   · measures upward flick velocity for the scroll-up trigger.
     */
    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;

      window.requestAnimationFrame(() => {
        this.scrollTicking = false;

        const y = window.scrollY;
        const now = performance.now();
        const doc = document.documentElement;
        const height = Math.max(1, doc.scrollHeight - window.innerHeight);
        this.maxScrollDepth = Math.max(
          this.maxScrollDepth,
          Math.min(100, Math.round((y / height) * 100))
        );

        const dt = now - this.lastScrollT;
        const dy = this.lastScrollY - y; // positive = moving up
        if (dt > 0 && dt < 160 && dy > 0 && this.cfg.triggerScrollUp && this.env.coarse) {
          const velocity = (dy / dt) * 1000; // px per second
          const nearTop = y < window.innerHeight * 1.5;
          if (velocity >= this.cfg.scrollUpVelocity && nearTop) {
            this.maybeOpen('rapid_scroll_up');
          }
        }

        this.lastScrollY = y;
        this.lastScrollT = now;
      });
    }

    onMouseOut(event) {
      // Fires when the pointer leaves the document toward the browser chrome.
      if (!event.relatedTarget && event.clientY <= 0) {
        this.maybeOpen('mouse_exit');
      }
    }

    /** Funnel signals: add-to-cart and checkout intent, kept in localStorage. */
    onDocClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button:not([type="button"])')) {
        local.set(KEYS.addedToCart, '1');
      }
      if (target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]')) {
        local.set(KEYS.reachedCheckout, '1');
      }
    }

    // ------------------------------------------------------------------------
    // Gatekeeper
    // ------------------------------------------------------------------------
    maybeOpen(reason) {
      if (!this.armed || this.isOpen || this.submitted) return;
      if (this.hasShown && this.cfg.oncePerSession) {
        this.log(`Trigger "${reason}" ignored — already shown this session.`);
        return;
      }

      const seconds = this.timeOnPageMs() / 1000;
      if (seconds < this.cfg.minTimeOnPage) {
        this.log(`Trigger "${reason}" ignored — ${seconds.toFixed(1)}s on page.`);
        return;
      }
      if (this.maxScrollDepth < this.cfg.minScrollDepth) {
        this.log(`Trigger "${reason}" ignored — ${this.maxScrollDepth}% scrolled.`);
        return;
      }
      this.open(reason);
    }

    timeOnPageMs() {
      const live =
        document.visibilityState === 'visible'
          ? performance.now() - this.visibleSince
          : 0;
      return this.activeMs + live;
    }

    // ------------------------------------------------------------------------
    // Dialog lifecycle
    // ------------------------------------------------------------------------
    open(reason = 'manual') {
      if (this.isOpen) return;
      this.isOpen = true;
      this.hasShown = true;
      this.openReason = reason;
      this.log('Opening', reason);

      session.set(KEYS.shown, '1');
      local.set(KEYS.lastShown, String(Date.now()));

      this.lastFocused = document.activeElement;
      this.lockScroll();

      this.root.hidden = false;
      // Double rAF: first frame paints the closed state, second starts the
      // transition — guarantees the entrance animates from translateY(102%).
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.root.classList.add('cw-is-open');
        });
      });

      this.sheet.focus({ preventScroll: true });
      document.addEventListener('keydown', this.onKeydown);

      // While open, one back gesture should close the dialog, not the page.
      if (this.env.coarse && !this.designMode) {
        try {
          history.pushState({ cwExitDialog: true }, '', location.href);
          this.dialogStatePushed = true;
        } catch (_) {
          /* non-fatal */
        }
      }
    }

    close(fromHistory = false) {
      if (!this.isOpen || this.isClosing) return;

      // Consume our dialog history entry first; the resulting popstate
      // re-enters close(true) and performs the visual dismissal.
      if (this.dialogStatePushed && !fromHistory) {
        if (this.awaitingHistoryClose) return;
        this.awaitingHistoryClose = true;
        history.back();
        return;
      }
      this.awaitingHistoryClose = false;

      this.isClosing = true;
      window.clearTimeout(this.autocloseTimer);
      this.root.classList.add('cw-is-closing');
      this.root.classList.remove('cw-is-open');

      const duration = (Number(this.cfg.animSpeed) || 550) * 0.6 + 80;
      window.setTimeout(() => {
        this.root.classList.remove('cw-is-closing');
        this.root.hidden = true;
        this.isOpen = false;
        this.isClosing = false;
        this.unlockScroll();
        document.removeEventListener('keydown', this.onKeydown);
        if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
          this.lastFocused.focus({ preventScroll: true });
        }
      }, duration);
    }

    /**
     * Body scroll lock without layout jump. `position: fixed` with a stored
     * offset is the only technique that also stops iOS rubber-banding; the
     * offset is a runtime value, hence the two inline style writes.
     */
    lockScroll() {
      this.lockScrollY = window.scrollY;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this.lockScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }

    unlockScroll() {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, this.lockScrollY);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.cfg.enableEscClose && !this.env.coarse) {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap
      const focusables = this.sheet.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // ------------------------------------------------------------------------
    // Form behavior
    // ------------------------------------------------------------------------
    bindFormEvents() {
      if (!this.form) return;

      if (this.overlay && this.cfg.enableOverlayClose) {
        this.overlay.addEventListener('click', () => this.close());
      }
      this.root.querySelectorAll('[data-cw-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.form.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.matches('[data-cw-option]')) return;

        this.submitBtn.disabled = false;
        const opensTextarea =
          this.cfg.enableTextarea && input.hasAttribute('data-cw-opens-textarea');
        this.toggleTextarea(opensTextarea);
      });

      if (this.textarea) {
        this.textarea.addEventListener('input', () => this.updateCounter());
        this.updateCounter();
      }

      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    toggleTextarea(open) {
      if (!this.reveal || !this.textarea) return;
      this.reveal.classList.toggle('cw-exit__reveal--open', open);
      this.textarea.disabled = !open;
      if (open) {
        // Let the grid-rows transition begin before focusing.
        window.setTimeout(() => this.textarea.focus({ preventScroll: false }), 260);
      } else {
        this.textarea.value = '';
        this.updateCounter();
      }
    }

    updateCounter() {
      if (!this.counter || !this.textarea) return;
      const max = Number(this.cfg.textareaMaxlength) || 500;
      const remaining = max - this.textarea.value.length;
      this.counter.textContent = String(remaining);
      this.counter.classList.toggle('cw-is-low', remaining <= Math.ceil(max * 0.1));
    }

    // ------------------------------------------------------------------------
    // Submission
    // ------------------------------------------------------------------------
    async handleSubmit(event) {
      event.preventDefault();
      if (this.submitted) return;

      const selected = this.form.querySelector('[data-cw-option]:checked');
      if (!selected) return;

      this.submitted = true;
      this.submitBtn.classList.add('cw-is-loading');
      this.submitBtn.setAttribute('aria-busy', 'true');

      const payload = this.buildPayload(selected.value);
      this.log('Payload', payload);

      const tasks = [wait(650)]; // minimum spinner beat — calm, not abrupt
      if (this.cfg.web3formsKey) {
        tasks.push(this.sendViaWeb3Forms(payload));
      }
      if (this.cfg.enableEmail && this.contactForm) {
        tasks.push(this.sendViaContactForm(payload));
      }
      this.dispatchAnalytics(payload);

      await Promise.allSettled(tasks);

      local.set(KEYS.submittedAt, String(Date.now()));
      this.showSuccess();
    }

    buildPayload(answer) {
      const now = new Date();
      const timezone =
        (Intl.DateTimeFormat().resolvedOptions() || {}).timeZone || 'Unknown';
      const utm = this.readUtm();

      return {
        date: now.toLocaleDateString(this.cfg.locale || 'en'),
        time: now.toLocaleTimeString(this.cfg.locale || 'en'),
        iso: now.toISOString(),
        productTitle: this.cfg.productTitle || document.title,
        productUrl: this.cfg.productUrl || location.href.split('#')[0],
        answer,
        customAnswer:
          this.textarea && !this.textarea.disabled ? this.textarea.value.trim() : '',
        trigger: this.openReason,
        device: this.env.device,
        browser: this.env.browser,
        os: this.env.os,
        country: this.cfg.shopCountry || '',
        timezone,
        language: navigator.language || '',
        referrer: session.get(KEYS.landingRef) || document.referrer || 'Direct',
        utm,
        sessionId: session.get(KEYS.sessionId) || '',
        viewport: `${window.innerWidth} × ${window.innerHeight}`,
        screen: `${window.screen.width} × ${window.screen.height} @${window.devicePixelRatio || 1}x`,
        addedToCart: local.get(KEYS.addedToCart) === '1',
        reachedCheckout: local.get(KEYS.reachedCheckout) === '1',
        timeOnPage: formatClock(this.timeOnPageMs()),
        scrollDepth: `${this.maxScrollDepth}%`
      };
    }

    formatEmailBody(p) {
      const yesNo = (v) => (v ? 'Yes' : 'No');
      const utmLines = Object.keys(p.utm).length
        ? Object.entries(p.utm)
            .map(([k, v]) => `  ${k}: ${v}`)
            .join('\n')
        : '  —';

      return [
        'CEDARWEL · EXIT SURVEY RESPONSE',
        '────────────────────────────────',
        `Answer:           ${p.answer}`,
        `Custom answer:    ${p.customAnswer || '—'}`,
        `Trigger:          ${p.trigger}`,
        '',
        `Product:          ${p.productTitle}`,
        `URL:              ${p.productUrl}`,
        `Date:             ${p.date}`,
        `Time:             ${p.time} (${p.timezone})`,
        '',
        'VISITOR',
        `Device:           ${p.device}`,
        `Browser:          ${p.browser}`,
        `OS:               ${p.os}`,
        `Country:          ${p.country || 'Unknown (see timezone/language)'}`,
        `Language:         ${p.language}`,
        `Viewport:         ${p.viewport}`,
        `Screen:           ${p.screen}`,
        '',
        'JOURNEY',
        `Referral source:  ${p.referrer}`,
        'UTM parameters:',
        utmLines,
        `Session ID:       ${p.sessionId}`,
        `Time on page:     ${p.timeOnPage}`,
        `Scroll depth:     ${p.scrollDepth}`,
        `Added to cart:    ${yesNo(p.addedToCart)}`,
        `Reached checkout: ${yesNo(p.reachedCheckout)}`,
        '',
        `Intended inbox:   ${this.cfg.notifyEmail}`,
        `Sent:             ${p.iso}`
      ].join('\n');
    }

    /**
     * PRIMARY delivery — Web3Forms, a free form-to-email relay built for
     * exactly this case: browser code that must send an email. Unlike the
     * Shopify contact form, it is CORS-enabled and returns a verifiable
     * success response. Configured with one access key in the Theme Editor;
     * without a key this channel is skipped entirely.
     */
    async sendViaWeb3Forms(payload) {
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            access_key: this.cfg.web3formsKey,
            subject: `Cedarwel exit survey · ${payload.answer}`,
            from_name: 'Cedarwel Exit Survey',
            botcheck: '',
            message: this.formatEmailBody(payload),
            answer: payload.answer,
            custom_answer: payload.customAnswer || '—',
            product: payload.productTitle,
            trigger: payload.trigger,
            session_id: payload.sessionId
          })
        });
        const result = await response.json().catch(() => ({}));
        this.log(
          result.success
            ? 'Web3Forms delivered ✓ — check the inbox that created the access key.'
            : 'Web3Forms rejected ✕ — is the access key correct?',
          result
        );
      } catch (error) {
        this.log('Web3Forms request failed ✕', error);
      }
    }

    /**
     * SECONDARY delivery — Shopify's native contact form. Kept because it is
     * the only zero-third-party channel, but know its ceiling: Shopify's bot
     * protection may divert scripted posts to a /challenge page and silently
     * drop them, even with hCaptcha disabled. Debug mode reports exactly
     * that, so a blocked store is visible instead of a mystery.
     */
    async sendViaContactForm(payload) {
      try {
        const data = new FormData(this.contactForm);
        data.set('contact[email]', this.cfg.notifyEmail);
        data.set('contact[body]', this.formatEmailBody(payload));
        data.set('contact[Survey answer]', payload.answer);
        data.set('contact[Product]', payload.productTitle);
        data.set('contact[Trigger]', payload.trigger);

        const response = await fetch(this.contactForm.action, {
          method: 'POST',
          body: data,
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (response.url && response.url.indexOf('/challenge') !== -1) {
          this.log(
            'Contact form BLOCKED ✕ — Shopify bot challenge intercepted the post. ' +
              'This channel cannot deliver from a script on this store; use the Web3Forms key.'
          );
        } else {
          this.log(
            'Contact form accepted (final delivery still subject to Shopify spam filtering).',
            response.status
          );
        }
      } catch (error) {
        this.log('Contact form post failed ✕', error);
      }
    }

    dispatchAnalytics(payload) {
      if (!this.cfg.enableAnalytics) return;
      const event = {
        event: 'cedarwel_exit_survey',
        surveyAnswer: payload.answer,
        surveyCustomAnswer: payload.customAnswer,
        surveyTrigger: payload.trigger,
        surveySessionId: payload.sessionId,
        surveyProduct: payload.productTitle
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);
      document.dispatchEvent(
        new CustomEvent('cedarwel:exit-survey:submit', { detail: payload })
      );
      this.log('Analytics dispatched.', event);
    }

    showSuccess() {
      this.submitBtn.classList.remove('cw-is-loading');
      this.submitBtn.removeAttribute('aria-busy');

      if (this.successPanel) {
        this.successPanel.hidden = false;
        window.requestAnimationFrame(() => {
          this.root.classList.add('cw-is-done');
        });
        this.successPanel.setAttribute('tabindex', '-1');
        window.setTimeout(() => this.successPanel.focus({ preventScroll: true }), 350);
      }

      const autoclose = Number(this.cfg.successAutoclose) || 0;
      if (autoclose > 0) {
        this.autocloseTimer = window.setTimeout(() => this.close(), autoclose * 1000);
      }
    }

    // ------------------------------------------------------------------------
    // Context capture
    // ------------------------------------------------------------------------
    ensureSessionId() {
      if (!session.get(KEYS.sessionId)) session.set(KEYS.sessionId, uuid());
    }

    captureUtm() {
      const params = new URLSearchParams(location.search);
      const found = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(
        (key) => {
          const value = params.get(key);
          if (value) found[key] = value;
        }
      );
      if (Object.keys(found).length) {
        const existing = this.readUtm();
        session.set(KEYS.utm, JSON.stringify({ ...existing, ...found }));
      }
    }

    readUtm() {
      try {
        return JSON.parse(session.get(KEYS.utm) || '{}');
      } catch (_) {
        return {};
      }
    }

    captureLandingReferrer() {
      if (!session.get(KEYS.landingRef)) {
        session.set(KEYS.landingRef, document.referrer || 'Direct');
      }
    }

    // ------------------------------------------------------------------------
    // Presentation plumbing
    // ------------------------------------------------------------------------
    assignStaggerIndexes() {
      this.root.querySelectorAll('.cw-anim').forEach((el, index) => {
        el.style.setProperty('--cw-i', String(index));
      });
    }

    applyReducedMotion() {
      const prefers = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.cfg.respectReducedMotion && prefers) {
        this.root.classList.add('cw-reduced');
      }
    }

    /** Live preview inside the Theme Editor: select the section → see it. */
    bindDesignMode() {
      if (!this.designMode) return;
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target && event.target.contains(this.root)) this.open('editor_preview');
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target && event.target.contains(this.root)) this.close();
      });
    }

    /** Small QA surface — see README “Testing”. */
    publicApi() {
      return {
        open: (reason = 'manual') => this.open(reason),
        close: () => this.close(),
        reset: () => {
          try {
            sessionStorage.removeItem(KEYS.shown);
            localStorage.removeItem(KEYS.lastShown);
            localStorage.removeItem(KEYS.submittedAt);
          } catch (_) {
            /* noop */
          }
          this.log('Caps cleared — reload to re-arm.');
        }
      };
    }
  }

  // --------------------------------------------------------------------------
  // Bootstrap
  // --------------------------------------------------------------------------
  const boot = () => {
    document.querySelectorAll('[data-cw-exit-root]').forEach((root) => {
      if (root.dataset.cwInit) return;
      root.dataset.cwInit = '1';
      const instance = new ExitSurvey(root);
      if (instance && instance.cfg) {
        window.CedarwelExitSurvey = instance.publicApi();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();